import { useEffect, useState, RefObject } from 'react';

export type LandingVisualPhase = 'hero' | 'rapper' | 'producer' | 'listener' | 'cta';

const PHASE_ORDER: LandingVisualPhase[] = ['hero', 'rapper', 'producer', 'listener', 'cta'];

/**
 * Picks the most visible landing section to drive scroll-linked hero graphics.
 */
export function useInViewSection(
  sectionRefs: Record<LandingVisualPhase, RefObject<HTMLElement | null>>
) {
  const [phase, setPhase] = useState<LandingVisualPhase>('hero');

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const ratios = new Map<LandingVisualPhase, number>();

    const pickPhase = () => {
      let best: LandingVisualPhase = 'hero';
      let bestRatio = 0;
      PHASE_ORDER.forEach((key) => {
        const r = ratios.get(key) ?? 0;
        if (r > bestRatio) {
          bestRatio = r;
          best = key;
        }
      });
      setPhase(best);
    };

    PHASE_ORDER.forEach((key) => {
      const el = sectionRefs[key].current;
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          ratios.set(key, entry.intersectionRatio);
          pickPhase();
        },
        { threshold: [0, 0.15, 0.35, 0.55, 0.75, 1], rootMargin: '-20% 0px -35% 0px' }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  // sectionRefs object is recreated each render; refs inside are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRefs.hero, sectionRefs.rapper, sectionRefs.producer, sectionRefs.listener, sectionRefs.cta]);

  return phase;
}
